import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { device } from '../../../utils/constants';
import {
  LabBottomRule,
  labAos,
  labBodyMobile,
  labCaptionMobile,
  labSectionPadMobile,
  labTitleMobile,
} from './labShared';

const base = process.env.PUBLIC_URL || '';

const Section = styled.section`
  color: #ffffff;
  font-family: 'TelegrafRegular', Helvetica, Arial, sans-serif;
  padding-top: 5vw;
  box-sizing: border-box;
  ${labSectionPadMobile}
`;

const Inner = styled.div`
  max-width: min(86rem, 100%);
  margin: 0 auto;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: clamp(1rem, 3vw, 2rem);

  @media ${device.mobile} {
    flex-direction: column;
    gap: clamp(1.25rem, 4vw, 1.75rem);
  }
`;

const TitleBlock = styled.div`
  flex: 1 1 min(100%, 28rem);
  min-width: 0;
`;

const MainTitle = styled.h2`
  margin: 0;
  font-family: 'Horizon';
  font-weight: 700;
  font-size: 2.4vw;
  letter-spacing: 0.14em;
  line-height: 1.2;
  text-transform: uppercase;
`;

const TitleLine = styled.span`
  display: block;
  font-family: 'Horizon';
  font-size: 3vw;
  ${labTitleMobile}

  @media ${device.mobile} {
    font-size: 2em;
  }
`;

const Subheading = styled.p`
  margin: 0;
  flex: 0 1 auto;
  align-self: flex-start;
  max-width: 100%;
  font-weight: 400;
  font-size: 1.2vw;
  font-family: 'TelegrafLight';
  line-height: 1.45;
  text-align: left;
  margin-top: 5vw;
  ${labBodyMobile}

  @media ${device.mobile} {
    margin-top: 0;
    align-self: stretch;
    max-width: none;
  }
`;

const PosterGrid = styled.div`
  margin-top: clamp(2rem, 5vw, 3.25rem);
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: clamp(1rem, 2.5vw, 2rem);
  align-items: start;

  @media ${device.mobile} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: clamp(1rem, 4vw, 1.5rem);
    margin-top: clamp(1.5rem, 6vw, 2.5rem);
  }
`;

const PosterCell = styled.article`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-width: 0;
`;

const PosterImg = styled.img`
  width: 100%;
  aspect-ratio: 2 / 3;
  object-fit: cover;
  display: block;
`;

const CaptionPrimary = styled.p`
  margin: 1.75vw 0 0;
  font-size: 1vw;
  font-family: 'TelegrafUltraBold';
  line-height: 1.35;
  ${labCaptionMobile}

  @media ${device.mobile} {
    margin-top: clamp(0.5rem, 2vw, 0.75rem);
  }
`;

const CaptionSecondary = styled.p`
  margin: 0.35rem 0 0;
  font-size: 1vw;
  font-family: 'TelegrafLight';
  line-height: 1.45;
  opacity: 0.92;
  ${labCaptionMobile}
`;

const BottomBlock = styled.div`
  padding-top: clamp(2.5rem, 6vw, 4rem);
  padding-bottom: clamp(1.25rem, 3vw, 2rem);

  @media ${device.mobile} {
    padding-top: clamp(2rem, 6vw, 3rem);
  }
`;

const CinemaLab = () => {
  const { t } = useTranslation();

  const FILMS = [
    {
      src: `${base}/img/lab/cinema/el-inca.png`,
      captionPrimary: t('lab.cinema.items.01.title'),
      captionSecondary: t('lab.cinema.items.01.body'),
    },
    {
      src: `${base}/img/lab/cinema/la-virgen-negra.png`,
      captionPrimary: t('lab.cinema.items.02.title'),
      captionSecondary: t('lab.cinema.items.02.body'),
    },
    {
      src: `${base}/img/lab/cinema/la-danubio.png`,
      captionPrimary: t('lab.cinema.items.03.title'),
      captionSecondary: t('lab.cinema.items.03.body'),
    },
    {
      src: `${base}/img/lab/cinema/fashionably-late.png`,
      captionPrimary: t('lab.cinema.items.04.title'),
      captionSecondary: t('lab.cinema.items.04.body'),
    },
  ];

  return (
    <Section aria-labelledby="lab-cinema-heading">
      <Inner>
        <HeaderRow>
          <TitleBlock {...labAos('fade-up')}>
            <MainTitle id="lab-cinema-heading">
              <TitleLine
                dangerouslySetInnerHTML={{
                  __html: t('lab.cinema.title'),
                }}
              />
              <TitleLine>{t('lab.cinema.premium')}</TitleLine>
            </MainTitle>
          </TitleBlock>
          <Subheading {...labAos('fade-up', 150)}>
            {t('lab.cinema.from')}
          </Subheading>
        </HeaderRow>

        <PosterGrid>
          {FILMS.map((film, index) => (
            <PosterCell key={film.src} {...labAos('fade-up', index * 120)}>
              <PosterImg
                src={film.src}
                alt=""
                loading="lazy"
                decoding="async"
              />
              <CaptionPrimary>{film.captionPrimary}</CaptionPrimary>
              <CaptionSecondary>{film.captionSecondary}</CaptionSecondary>
            </PosterCell>
          ))}
        </PosterGrid>
      </Inner>

      <BottomBlock>
        <LabBottomRule aria-hidden />
      </BottomBlock>
    </Section>
  );
};

export default CinemaLab;
